import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { supabase } from '@/integrations/supabase/client';
import { floorTicketChangeMatters, realtimeRecoveryAction, rebuildDelayMs, uniqueChannelTopic } from '@/lib/realtime/floorRealtime';
import { useForegroundRecovery } from '@/hooks/useForegroundRecovery';
import { Button } from '@/components/ui/button';
import { Settings, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useFormFactor } from '@/hooks/use-mobile';
import { useOutdoorWeatherAdvisory } from '@/hooks/weather/useOutdoorWeatherAdvisory';
import { bayTile } from '@/lib/location/floorPlacement';
import { placeByLegacyProjection, placeByPhysicalTruth } from '@/lib/location/model';
import { readShopCurrentLocations } from '@/lib/location/api';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
import {
  BayWeatherMarker,
  OutdoorWeatherAdvisory,
} from '@/components/weather/OutdoorWeatherAdvisory';

const ShopFloor = () => {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const formFactor = useFormFactor();
  const isTouchDevice = formFactor !== 'desktop';
  const [bays, setBays] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [zoom, setZoom] = useState(0.6);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const floorRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origPanX: number; origPanY: number } | null>(null);

  const handleZoomIn = useCallback(() => setZoom(z => Math.min(z + 0.1, 2)), []);
  const handleZoomOut = useCallback(() => setZoom(z => Math.max(z - 0.1, 0.2)), []);
  const handleZoomReset = useCallback(() => { setZoom(0.6); setPan({ x: 0, y: 0 }); }, []);

  const onFloorWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    setZoom(z => Math.min(2, Math.max(0.2, z - e.deltaY * 0.002)));
  }, []);

  // Drag-to-pan handlers
  const onPanPointerDown = useCallback((e: React.PointerEvent) => {
    // Only pan on the background, not on bay cards
    if ((e.target as HTMLElement).closest('[data-bay-card]')) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origPanX: pan.x, origPanY: pan.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [pan]);

  const onPanPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({ x: dragRef.current.origPanX + dx, y: dragRef.current.origPanY + dy });
  }, []);

  const onPanPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // CORE-BR: the shop's weather policy, evaluated server-side against the
  // current reading, annotated onto the bays it actually applies to.
  //
  // Annotated, not filtered. Every bay stays on the floor and stays
  // selectable; the advisory explains the shop's own rule and the reading
  // that crosses it, and a person decides what to do about it.
  const { assessment: weatherAssessment, advisories: weatherAdvisories } =
    useOutdoorWeatherAdvisory(bays);
  const weatherAdvisoryByBay = useMemo(
    () => new Map(weatherAdvisories.map((a) => [a.bayId, a])),
    [weatherAdvisories],
  );

  const loadBays = useCallback(async () => {
    if (!activeShop) return;
    const { data } = await supabase.from('shop_bays').select('*').eq('shop_id', activeShop.id).eq('is_active', true);
    if (data) setBays(data);
  }, [activeShop?.id]);

  const loadTickets = useCallback(async () => {
    if (!activeShop) return;
    const { data } = await supabase.from('tickets').select(`
      *,
      vehicle:vehicles(id, year, make, model, engine_size),
      ticket_jobs(minutes_override, notes, job:jobs(*)),
      technician:technicians!tickets_assigned_tech_id_fkey(id, name, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, shift_end)
    `)
      // DMC-D3b: canonical active-ticket filter — both reps excluded.
      // Status IN excludes Completed/Deleted; .is('deleted_at', null) is
      // defense-in-depth atop the DMC-D1 primitive fix.
      .eq('shop_id', activeShop.id).not('status', 'in', '("Completed","Deleted")').is('deleted_at', null);
    // Stage B/F: placement follows current physical truth, never a plan.
    const currentByTicket = await readShopCurrentLocations(activeShop.id).catch((e: unknown) => {
      console.error('[ShopFloor] current vehicle locations unavailable', e);
      return null;
    });

    if (data) {
      const placed = data.map(t => (currentByTicket
        ? placeByPhysicalTruth(t, currentByTicket)
        : placeByLegacyProjection(t)));
      const transformed = placed.map(t => ({
        ...t,
        jobs: t.ticket_jobs?.map((tj: any) => ({
          ...tj.job,
          standardMinutes: tj.minutes_override || tj.job.standard_minutes,
          notes: tj.notes
        })) || [],
        vehiclePresent: isVehiclePresent(t),
        predictedMinutes: t.predicted_minutes,
        assignedTechId: t.assigned_tech_id,
        pauseReason: t.pause_reason,
        startTime: new Date(t.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      }));
      setTickets(transformed);
    }
  }, [activeShop?.id]);

  const loadTechnicians = useCallback(async () => {
    if (!activeShop) return;
    const { data } = await supabase.from('technicians').select('*').eq('shop_id', activeShop.id);
    if (data) setTechnicians(data);
  }, [activeShop?.id]);

  // Stage 0D — one authoritative reload of everything the floor renders.
  const refreshFloor = useCallback(async () => {
    await Promise.all([loadBays(), loadTickets(), loadTechnicians()]);
  }, [loadBays, loadTickets, loadTechnicians]);

  // Stage 0D — server truth again when the phone / tab comes back.
  useForegroundRecovery(() => { void refreshFloor(); }, { enabled: !!activeShop?.id });

  // Stage 0D — bumped to re-subscribe after the client drops the channel.
  const [realtimeEpoch, setRealtimeEpoch] = useState(0);
  // Consecutive abnormal closes, for the bounded re-subscribe backoff.
  const rebuildAttemptsRef = useRef(0);

  useEffect(() => {
    const loadAll = async () => {
      try {
        await refreshFloor();
      } finally {
        setIsLoading(false);
      }
    };
    loadAll();
    // Stage 0D: no shop, nothing to subscribe to (was "shopfloor-realtime-undefined").
    if (!activeShop?.id) return;

    // Scoped realtime with targeted refresh.
    //
    // RTC-A4: collapse the two pre-A4 page-level channels
    //   (`shopfloor-bays-...`, `shopfloor-tickets-...`)
    // into ONE consolidated channel with two `.on('postgres_changes', ...)`
    // listener registrations. Pre-A4 each ShopFloor mount opened TWO
    // websocket subscriptions for the same shop scope and the same
    // useEffect lifecycle — every event therefore traversed two
    // independent channel objects. Post-A4 the same two listener
    // bodies are registered against ONE channel; behavior is preserved
    // verbatim because Supabase JS dispatches each
    // `.on('postgres_changes', ...)` listener independently regardless
    // of how many channels host it. The listener bodies below are
    // byte-identical to the pre-A4 versions (including the RFS-A2
    // 300ms debounce wrapper, the RFS-A3 11-field scheduling gate, the
    // FIX-R34/R35/R36 markers, the client-side shop guard, and the
    // simple `() => loadBays()` refetch closure); only the channel-
    // creation + `.subscribe(...)` + `removeChannel(...)` boilerplate
    // was consolidated 2→1. The status callback is also consolidated:
    // pre-A4 each of the two `.subscribe(...)` calls had its own
    // `(status, err) => console.error(...)` log line; post-A4 a
    // SINGLE callback covers both listeners. The pre-A4 per-listener
    // log identifiers are preserved in this doctrinal block (FIX-R36-
    // LOW-1 + FIX-R35-MED-1) so a future regression that re-introduces
    // per-channel `.subscribe` can re-anchor on the original markers.
    //
    // OUT OF SCOPE — explicitly NOT merged in this round:
    //   - `useReminderTriggers` / `ReminderTriggerProvider`
    //     (App.tsx-mounted around the page, not page-mounted).
    //
    // Pre-A4 channel names (preserved here as a doctrinal anchor for
    // the closure test §A — the test scans for them as REMOVED):
    //   `shopfloor-bays-${shop_id}`
    //   `shopfloor-tickets-${shop_id}`
    //
    // Post-A4 channel name:
    //   `shopfloor-realtime-${shop_id}`
    // ShopFloor does NOT include `selectedDayLabel` in its useEffect
    // deps (different from Birdseye + Dashboard), so the channel name
    // stays date-agnostic. The merged channel rebuilds on shop change
    // exactly as the two pre-A4 channels did.

    // RFS-A2: debounce loadTickets refetch on the tickets channel. Pre-A2
    // every ticket event fired a full SHOP-WIDE `loadTickets()` (no date
    // filter on the query — could be 500+ rows × 3 joins in busy shops).
    // Under cascade fan-out this was the worst-shape demo-lag culprit
    // among realtime consumers. Debouncing 300ms collapses bursts into
    // one refetch; mirrors Birdseye's RFS-A2 wrapper for cross-page
    // pattern consistency. useLiveReshuffle's 10s cascade-debounce
    // upstream still serializes the cascade waves themselves.
    // (RTC-A4: declaration site preserved BEFORE the channel chain so
    // the helper is hoisted into the listener closure unchanged.)
    let ticketsRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleTicketsRefetch = () => {
      if (ticketsRefetchTimer) clearTimeout(ticketsRefetchTimer);
      ticketsRefetchTimer = setTimeout(() => {
        ticketsRefetchTimer = null;
        loadTickets();
      }, 300);
    };
    // Stage 0D — after a (re)join, events may have been missed: reload all.
    let floorRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleFloorRefetch = () => {
      if (floorRefetchTimer) clearTimeout(floorRefetchTimer);
      floorRefetchTimer = setTimeout(() => {
        floorRefetchTimer = null;
        void refreshFloor();
      }, 300);
    };
    let rebuildTimer: ReturnType<typeof setTimeout> | null = null;

    const shopfloorChannel = supabase
      .channel(uniqueChannelTopic(`shopfloor-realtime-${activeShop.id}`))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shop_bays', filter: `shop_id=eq.${activeShop?.id}` }, () => loadBays())
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tickets',
        // R36: Server-side scope so only this shop's events stream to the
        // client. Previously every shop's ticket events were broadcast and
        // discarded client-side at line 113 — wasted bandwidth + cross-
        // tenant leak risk if the client-side guard ever regresses. The
        // guard below is retained as defense-in-depth (mirrors the R25
        // pattern in Birdseye.tsx).
        filter: `shop_id=eq.${activeShop?.id}`,
      }, (payload) => {
        const row = (payload.new || payload.old) as any;
        if (!row?.shop_id || row.shop_id !== activeShop?.id) return;

        // RFS-A3 / Stage 0D: rendered-field gate, shared with Birdseye
        // (FLOOR_TICKET_FIELDS). INSERT/DELETE always refetch.
        if (!floorTicketChangeMatters(payload)) return;

        // RFS-A2: schedule a debounced refetch instead of firing immediately.
        scheduleTicketsRefetch();
      })
      // Stage B/F: a physical move changes current truth — that moves a car.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_location_current', filter: `shop_id=eq.${activeShop?.id}` }, () => scheduleTicketsRefetch())
      .subscribe((status, err) => {
        // RTC-A4: consolidated status callback covers both listeners
        // (shop_bays / tickets). Pre-A4 each of the two channels had
        // its own `(status, err) => console.error(...)` callback —
        // FIX-R36-LOW-1 (shop_bays) + FIX-R35-MED-1 (tickets). Post-
        // A4 a single callback surfaces drops for the entire merged
        // channel; the listener-binding error is carried in `err`.
        // Both pre-A4 doctrinal markers are preserved in the
        // doctrinal block above so future regression detection can
        // re-anchor.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[ShopFloor] realtime channel status:', status, err);
        }
        // Stage 0D: every (re)join reloads what may have been missed; a
        // channel the client dropped for good is re-subscribed.
        const action = realtimeRecoveryAction(status, err);
        if (action === 'refetch') {
          rebuildAttemptsRef.current = 0;
          scheduleFloorRefetch();
        }
        if (action === 'rebuild' && !rebuildTimer) {
          const delay = rebuildDelayMs(rebuildAttemptsRef.current);
          if (delay !== null) {
            rebuildAttemptsRef.current += 1;
            rebuildTimer = setTimeout(() => setRealtimeEpoch((n) => n + 1), delay);
          }
        }
      });

    return () => {
      // RFS-A2: clear any pending debounced refetch on unmount / shop change
      // so the next mount starts with a clean timer and no stale loadTickets()
      // from the previous shop fires.
      if (ticketsRefetchTimer) clearTimeout(ticketsRefetchTimer);
      if (floorRefetchTimer) clearTimeout(floorRefetchTimer);
      if (rebuildTimer) clearTimeout(rebuildTimer);
      supabase.removeChannel(shopfloorChannel);
    };
  }, [activeShop?.id, loadBays, loadTickets, loadTechnicians, refreshFloor, realtimeEpoch]);

  const bayTypeColors: Record<string, string> = {
    '2-post': 'hsl(210, 100%, 50%)',
    '4-post': 'hsl(260, 100%, 60%)',
    'scissor': 'hsl(140, 100%, 45%)',
    'adas': 'hsl(30, 100%, 50%)',
    'alignment': 'hsl(340, 100%, 55%)',
    'wash': 'hsl(190, 100%, 50%)',
    'inspection': 'hsl(50, 100%, 55%)',
    'general': 'hsl(0, 0%, 50%)',
    'flat': 'hsl(0, 0%, 50%)',
  };

  const bayTypeLabels: Record<string, string> = {
    '2-post': '2-Post',
    '4-post': '4-Post',
    'scissor': 'Scissor',
    'adas': 'ADAS',
    'alignment': 'Alignment',
    'wash': 'Wash',
    'inspection': 'Inspection',
    'general': 'Flat',
    'flat': 'Flat',
  };

  // Stage B/F: the tile's occupant is physical truth only; the bay tech's
  // next scheduled job is shown as "Up next", never drawn as the car.
  const getBayTile = (bayId: string, assignedTechId: string | null) =>
    bayTile(bayId, assignedTechId, tickets);

  const getTechName = (techId: string) => {
    return technicians.find(t => t.id === techId)?.name || 'Unassigned';
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading shop floor...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-screen bg-background">
      <div className="tech-grid" />
      <div className="relative z-10 flex flex-col h-full">
      <TopBar />
      
      <main className="flex-1 overflow-hidden">
        {/* Header */}
        <div className="border-b bg-card px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Shop Floor View</h1>
            <p className="text-sm text-muted-foreground">Live bay and vehicle tracking</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/shop-floor/editor')}>
              <Settings className="h-4 w-4 mr-2" />
              Configure Layout
            </Button>
            <div className="flex items-center gap-1 ml-2 border-l pl-2">
              <Button variant="outline" size="sm" onClick={handleZoomOut}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
              <Button variant="outline" size="sm" onClick={handleZoomIn}>
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleZoomReset}>
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/*
          CORE-BR: shown only when there is something operationally relevant
          to say — a policy conflict, or the fact that weather could not be
          checked at all. Clear weather and a disabled policy render nothing,
          because a board people work from should not carry a notice that
          never changes.
        */}
        <div className="px-6 pt-3">
          <OutdoorWeatherAdvisory
            assessment={weatherAssessment}
            advisories={weatherAdvisories}
          />
        </div>

        {/* Shop Floor Grid */}
        <div
          ref={floorRef}
          className="flex-1 bg-muted/20 overflow-hidden select-none"
          style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
          onWheel={onFloorWheel}
          onPointerDown={onPanPointerDown}
          onPointerMove={onPanPointerMove}
          onPointerUp={onPanPointerUp}
        >
          {bays.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-lg text-muted-foreground mb-4">No bays configured yet</p>
              <Button onClick={() => navigate('/shop-floor/editor')}>
                <Settings className="h-4 w-4 mr-2" />
                Configure Shop Layout
              </Button>
            </div>
          ) : (
            <div
              className="relative"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                width: '3000px',
                height: '2000px',
              }}
            >
              {/* Bay Positions */}
              {bays.map(bay => {
                const { occupant: ticket, upNext } = getBayTile(bay.id, bay.assigned_tech_id);
                const techName = getTechName(bay.assigned_tech_id);

                return (
                  <div
                    key={bay.id}
                    className={cn(
                      "absolute rounded-lg border-2 p-3 transition-all hover:shadow-lg",
                      ticket ? "bg-card" : "bg-muted/50"
                    )}
                    style={{
                      left: `${bay.position_x}px`,
                      top: `${bay.position_y}px`,
                      width: `${bay.width}px`,
                      height: `${bay.height}px`,
                      borderColor: bayTypeColors[bay.bay_type] || 'hsl(0, 0%, 50%)'
                    }}
                  >
                    {/* Bay Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 min-w-0">
                        <h3 className="font-bold text-sm truncate">{bay.name}</h3>
                        {/* CORE-BR: marks outdoor bays the shop's policy
                            currently speaks to. Never hides the bay. */}
                        {weatherAdvisoryByBay.get(bay.id) && (
                          <BayWeatherMarker
                            advisory={weatherAdvisoryByBay.get(bay.id)!}
                          />
                        )}
                      </div>
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: bayTypeColors[bay.bay_type],
                          color: bayTypeColors[bay.bay_type]
                        }}
                      >
                        {bayTypeLabels[bay.bay_type] || bay.bay_type}
                      </Badge>
                    </div>

                    {/* Technician */}
                    <p className="text-xs text-muted-foreground mb-2">👤 {techName}</p>

                    {/* Vehicle/Ticket Info */}
                    {ticket ? (
                      <div className="mt-auto">
                        <div className="p-2 bg-primary/10 rounded border border-primary/20">
                          <p className="font-medium text-xs mb-1">{ticket.id}</p>
                          <p className="text-xs">
                            {ticket.vehicle.year} {ticket.vehicle.make} {ticket.vehicle.model}
                          </p>
                          {ticket.vehicle.license_plate && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {ticket.vehicle.license_plate}
                            </p>
                          )}
                          <Badge 
                            variant={ticket.status === 'Active' ? 'default' : 'secondary'}
                            className="mt-2 text-xs"
                          >
                            {ticket.status}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-16 text-muted-foreground">
                        <p className="text-xs">Empty</p>
                        {upNext && <p className="text-xs" data-testid="bay-up-next">Up next: {upNext.id}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      </div>
    </div>
  );
};

export default ShopFloor;
